/**
 * useMicroform - CyberSource Microform v2 hooks and utilities
 *
 * @module hooks/useMicroform
 * @memberof -Common
 */
import { useEffect, useMemo, useRef, useState } from 'react';

import { decorateCardType } from '../../utils/creditCard/creditCard';
import { isServer } from '../../utils/device_detection/device_detection';
import { handleEmptyObjects } from '../../utils/handleEmptyObjects/handleEmptyObjects';
import { useNextRender } from '../useNextRender/useNextRender';


/**
 * Module-level double-load guard.
 * Stores the promise from the first call so subsequent calls return the same one.
 * @type {Promise<void>|null}
 */
let microformScriptPromise = null;

/**
 * Singleton cache for the Microform instance.
 * CyberSource Microform v2 is intended to be initialized once per checkout session.
 * Re-initializing with the same capture context can lead to "CREATE_FIELD_DUPLICATE" errors.
 */
let cachedMicroform = null;
let cachedCaptureContext = null;

/**
     * Fields that are allowed to be updated via the native bridge.
    */
const ALLOWED_FIELDS = [
  'expirationDate',
  'firstName',
  'lastName',
  'address1',
  'address2',
  'city',
  'state',
  'postalCode',
  'phoneNumber'
];

/**
 * Decodes a JWT payload without a full library.
 *
 * @param {object} data - Object containing the JWT string
 * @returns {object} The decoded JSON payload
 */
export const decodeJwt = ( data ) => {
  const { token } = handleEmptyObjects( data );
  if( !token || typeof token !== 'string' ){
    return {};
  }
  const trimmed = token.trim();
  try {
    const parts = trimmed.split( '.' );
    if( parts.length !== 3 ){
      return {};
    }
    const base64Url = parts[1];
    const base64 = base64Url.replace( /-/g, '+' ).replace( /_/g, '/' );
    const jsonPayload = decodeURIComponent(
      window
        .atob( base64 )
        .split( '' )
        .map( ( c ) => {
          return '%' + ( '00' + c.charCodeAt( 0 ).toString( 16 ) ).slice( -2 );
        } )
        .join( '' )
    );

    return JSON.parse( jsonPayload );
  }
  catch ( e ){
    return {};
  }
};

/**
 * Loads the CyberSource Microform v2 SDK script from the URL specified in the capture context.
 * Implements a double-load guard (only loads once, returns cached promise on subsequent calls).
 *
 * @param {object} data - Object containing the capture context JWT
 * @returns {Promise<void>} Resolves when the script is loaded
 */
export const loadMicroformScript = ( data ) => {
  const { captureContext } = handleEmptyObjects( data );
  if( microformScriptPromise ){
    return microformScriptPromise;
  }

  if( !captureContext ){
    return Promise.reject( new Error( 'Capture context is required to load Microform SDK' ) );
  }

  try {
    const decoded = decodeJwt( { token: captureContext } );

    if( decoded?.exp && decoded.exp * 1000 < Date.now() ){
      return Promise.reject( new Error( 'Capture context has expired' ) );
    }

    const sdkUrl = decoded?.ctx?.[0]?.data?.clientLibrary;
    const integrity = decoded?.ctx?.[0]?.data?.clientLibraryIntegrity;

    if( !sdkUrl ){
      return Promise.reject( new Error( 'Capture context does not contain Microform SDK URL' ) );
    }

    microformScriptPromise = new Promise( ( resolve, reject ) => {
      const script = document.createElement( 'script' );
      script.src = sdkUrl;

      if( integrity ){
        script.integrity = integrity;
        script.crossOrigin = 'anonymous';
      }

      script.async = true;

      script.onload = () => {
        resolve();
      };

      script.onerror = () => {
        microformScriptPromise = null;
        reject( new Error( 'Failed to load CyberSource Microform SDK' ) );
      };

      document.head.appendChild( script );
    } );

    return microformScriptPromise;
  }
  catch ( e ){
    return Promise.reject( e );
  }
};

/**
 * Initializes a Microform instance from a capture context.
 * Returns a cached instance if the capture context matches the previous one
 * to prevent duplicate field errors.
 *
 * @param {object} data - Object containing the capture context JWT
 * @returns {object} The Microform instance (has createField, createToken methods)
 */
export const initMicroform = ( data ) => {
  const { captureContext } = handleEmptyObjects( data );
  try {
    if( !window.Flex ){
      throw new Error( 'CyberSource Microform SDK (window.Flex) not loaded. Call loadMicroformScript() first.' );
    }

    const decoded = decodeJwt( { token: captureContext } );
    if( decoded?.exp && decoded.exp * 1000 < Date.now() ){
      throw new Error( 'Capture context has expired' );
    }

    // Return cached instance if capture context is identical
    if( cachedMicroform && cachedCaptureContext === captureContext ){
      return cachedMicroform;
    }

    // If we have an existing cached instance with a different context, dispose of it first
    if( cachedMicroform && typeof cachedMicroform.dispose === 'function' ){
      try {
        cachedMicroform.dispose();
      }
      catch ( e ){
        // eslint-disable-next-line no-console
        console.warn( 'Failed to dispose existing Microform instance', e );
      }
    }

    const flex = new window.Flex( captureContext );
    cachedMicroform = flex.microform();
    cachedCaptureContext = captureContext;

    // Registry for tracking fields created on this specific instance
    cachedMicroform._fields = {};

    return cachedMicroform;
  }
  catch ( error ){
    throw new Error( `Failed to initialize Microform: ${ error.message }` );
  }
};

/**
 * Orchestrates the full initialization flow: load script -> init.
 * Uses the provided capture context from DXL.
 *
 * @param {object} data - Object containing the capture context JWT
 * @returns {Promise<object>} Resolves with the initialized Microform instance
 */
export const getMicroformInstance = async( data ) => {
  const { captureContext } = handleEmptyObjects( data );
  if( !captureContext ){
    throw new Error( 'Capture context is required to initialize Microform' );
  }

  await loadMicroformScript( { captureContext } );
  return initMicroform( { captureContext } );
};


/**
 * Ensures the Microform SDK is initialized exactly once per session and
 * provides the resulting instance to the component.
 *
 * @param {boolean} enabled - Whether to attempt initialization (e.g., transientToken flag)
 * @param {string} [captureContext] - Optional dynamic capture context JWT
 * @returns {object} { microform, ready, error }
 */
export const useMicroform = ( enabled, captureContext ) => {
  const [microform, setMicroform] = useState( null );
  const [ready, setReady] = useState( false );
  const [error, setError] = useState( null );
  const initializedRef = useRef( false );
  const prevCaptureContextRef = useRef( captureContext );

  if( prevCaptureContextRef.current !== captureContext ){
    initializedRef.current = false;
    prevCaptureContextRef.current = captureContext;
    setReady( false );
  }

  useEffect( () => {
    if( !enabled || !captureContext ){
      return;
    }

    // this change is required for focus out issue in microform
    if( initializedRef.current ){
      return;
    }
    let isMounted = true;
    getMicroformInstance( { captureContext } )
      .then( ( instance ) => {
        if( isMounted ){
          initializedRef.current = true;
          setMicroform( instance );
          setReady( true );
          setError( null );
        }
      } )
      .catch( ( err ) => {
        if( isMounted ){
          setError( err );
          setReady( false );
        }
      } );

    return () => {
      isMounted = false;
    };
  }, [enabled, captureContext] );

  return { microform, ready, error };
};

/**
 * Check if microform is loading
 * @param {object} data - Object containing attributes
 * @returns {boolean}
 */
export const isMicroformLoading = ( data ) => {
  const { transientToken, microformReady, selected, error, captureContext } = handleEmptyObjects( data );

  // If not using Microform, or if there's an error/unselected card, don't show the loader
  if( !transientToken || selected === false || error ){
    return false;
  }

  // Show loader if:
  // 1. The encryption key (captureContext) is still missing
  // 2. Iframes are not yet ready
  return !captureContext || typeof captureContext !== 'string' || !microformReady;
};

/**
 * Sends a message to the native bridge
 * @param {object} data - Object containing attributes
 */
export const postBridgeMessage = ( data ) => {
  const { method, ...rest } = handleEmptyObjects( data );
  if( isServer() || !method ){
    return;
  }

  const isAndroid = !!( global.microformBridge && typeof global.microformBridge[method] === 'function' );
  const isIos = !!( global.webkit?.messageHandlers?.nativeBridge );

  if( !isAndroid && !isIos ){
    return;
  }

  // Android
  if( isAndroid ){
    global.microformBridge[method]();
  }

  // iOS
  if( isIos ){
    global.webkit.messageHandlers.nativeBridge.postMessage( {
      method,
      ...rest
    } );
  }
};

/**
 * Specialized handler for token success
 * @param {object} data - Object containing attributes
 * @param {object} methods - Object containing method callbacks
 */
export const signalTokenSuccess = ( data ) => {
  const { token, expirationDate, creditcardType, isCvvOnly } = handleEmptyObjects( data );
  if( isServer() ){
    return;
  }

  const payload = {
    token,
    creditcardType
  };

  if( !isCvvOnly ){
    payload.expirationDate = expirationDate;
  }

  if( global.microformBridge && typeof global.microformBridge.onTokenSuccess === 'function' ){
    global.microformBridge.onTokenSuccess( JSON.stringify( payload ) );
  }

  if( global.webkit?.messageHandlers?.nativeBridge ){
    global.webkit.messageHandlers.nativeBridge.postMessage( {
      method: 'onTokenSuccess',
      data: token,
      ...payload
    } );
  }
};

/**
 * Registers a global callback for the native app to call
 * @param {object} data - Object containing attributes
 * @param {object} methods - Object containing method callbacks
 */
export const registerBridgeCallback = ( data, methods ) => {
  const { name } = handleEmptyObjects( data );
  const { callback } = handleEmptyObjects( methods );
  if( typeof global !== 'undefined' ){
    global[name] = callback;
  }
};

/**
 * Resolves card type for CVV only mode
 * @param {object} data - Object containing attributes
 */
export const resolveCvvOnlyCardType = ( data ) => {
  const { initDetails } = handleEmptyObjects( data );

  const supportedCards = initDetails?.creditCardInput?.creditCardForm?.supportedCards || [];
  const selectedTypeStr = initDetails?.creditCardInput?.savedCardType || initDetails?.selectedCard?.cardType || '';

  if( !selectedTypeStr ){
    return {};
  }

  const matchedCard = supportedCards.find( ( card ) => {
    return card.type?.toLowerCase() === selectedTypeStr.toLowerCase();
  } );

  if( !matchedCard ){
    return {};
  }

  return decorateCardType( { cardType: { ...matchedCard } } );
};

/**
 * Custom hook to handle publicKey refresh logic
 * @param {string} publicKey - Encryption key
 */
export const usePublicKeyRefresh = ( publicKey ) => {
  const isExpired = useMemo( () => {
    if( !publicKey || typeof publicKey !== 'string' ){
      return false;
    }

    try {
      const decoded = decodeJwt( { token: publicKey } );
      // Buffer of 60 seconds (14 mins for a 15 min key)
      return decoded?.exp && ( decoded.exp * 1000 - Date.now() < 60000 );
    }
    catch ( e ){
      return true;
    }
  }, [publicKey] );

  useEffect( () => {
    if( !isExpired ){
      return;
    }
    postBridgeMessage( { method: 'refreshCaptureContext' } );
  }, [isExpired] );
};

/**
 * Custom hook to handle card type state and resolution
 * @param {object} data - Arguments
 * @returns {array} [cardType, setCardType]
 */
export const useCardType = ( data ) => {
  const { isCvvOnly, initDetails } = handleEmptyObjects( data );
  const [cardType, setCardType] = useState( {} );

  useEffect( () => {
    if( !isCvvOnly ){
      return;
    }

    const decorated = resolveCvvOnlyCardType( { initDetails } );
    setCardType( decorated );
  }, [initDetails, isCvvOnly] );

  return [cardType, setCardType];
};

/**
 * Specialized handler for triggerMicroformSubmit native bridge call.
 * Orchestrates value updates, validation, and form submission.
 *
 * @param {object} data - Arguments object
 * @param {object} methods - Callback methods object
 */
export const handleTriggerMicroformSubmit = ( data, methods ) => {
  const { jsonPayload, formikRef, isCvvOnly } = handleEmptyObjects( data );
  const { scheduleNextRender } = handleEmptyObjects( methods ) ;
  if( !isCvvOnly ){
    formikRef.current?.submitForm?.();
    return;
  }

  // Immediately returns undefined to WebKit — no Promise returned
  ( async() => {
    let parsedPayload;
    try {
      parsedPayload = typeof jsonPayload === 'string' ? JSON.parse( jsonPayload ) : jsonPayload;
    }
    catch ( e ){
      parsedPayload = {};
    }

    if( formikRef.current && typeof parsedPayload === 'object' && parsedPayload !== null ){
      Object.keys( parsedPayload ).forEach( ( key ) => {
        if( ALLOWED_FIELDS.includes( key ) ){
          formikRef.current.setFieldValue( key, parsedPayload[key] );
        }
      } );
    }

    // Give React/Formik time to batch updates and flush to state
    if( formikRef.current && typeof scheduleNextRender === 'function' ){
      scheduleNextRender( async() => {
        const errors = await formikRef.current.validateForm();
        if( errors && Object.keys( errors ).length > 0 ){
          postBridgeMessage( { method: 'onTokenFailure' } );
          formikRef.current.setTouched( { cvv: true } );
        }
        else {
          formikRef.current.submitForm();
        }
      } );
    }

  } )();
};

/**
 * Custom hook to handle native bridge registration
 * @param {object} data - Arguments
 */
export const useBridgeRegistration = ( data ) => {
  const { setInitDetails, formikRef, isCvvOnly } = handleEmptyObjects( data );
  const [scheduleNextRender] = useNextRender();

  useEffect( () => {
    if( isServer() ){
      return;
    }

    registerBridgeCallback( { name: 'setInitDetails' }, { callback: ( jsonPayload ) => {
      try {
        const parsedPayload = typeof jsonPayload === 'string' ? JSON.parse( jsonPayload ) : jsonPayload;
        setInitDetails( parsedPayload );
      }
      catch ( e ){
        // eslint-disable-next-line no-console
        console.error( 'Error parsing setInitDetails payload', e );
      }
    } } );

    registerBridgeCallback( { name: 'triggerMicroformSubmit' }, { callback: ( jsonPayload ) => {
      handleTriggerMicroformSubmit( { jsonPayload, formikRef, isCvvOnly }, { scheduleNextRender } );
    } } );

    postBridgeMessage( { method: 'getInitDetails' } );
  }, [setInitDetails, formikRef, isCvvOnly, scheduleNextRender] );
};

export default useMicroform;
